/**
 * Getting into a recorded lesson and back out to the same library view.
 *
 * The library keeps its class, chapter, date, search, tab and page in its own
 * address, so the way back is simply that address. It travels in router state
 * rather than in the lesson's URL: it is a convenience for this tab's history,
 * not part of what the lesson page is, and a lesson link pasted elsewhere
 * should not carry somebody else's filters.
 */

/** Where the library lives when nobody says otherwise — a lesson opened from a link. */
export const LIBRARY_PATH = '/teacher/videos';

interface LessonLinkState {
  returnTo: string;
}

export function lessonPath(uuid: string): string {
  return `/teacher/recorded-classes/${uuid}`;
}

/** Router state for opening a lesson from the library view at `returnTo`. */
export function lessonLinkState(returnTo: string): LessonLinkState {
  return { returnTo };
}

/**
 * The library view to go back to, read from router state.
 *
 * Router state is `unknown` — it survives reloads and can be anything a
 * previous version of the app put there — so it is checked rather than cast,
 * and only an address inside the library is honoured: Back must never become a
 * way to send a teacher somewhere else.
 */
export function returnToOf(state: unknown): string {
  if (typeof state === 'object' && state !== null && 'returnTo' in state) {
    const { returnTo } = state;
    if (typeof returnTo === 'string' && returnTo.startsWith(`${LIBRARY_PATH}?`)) return returnTo;
  }
  return LIBRARY_PATH;
}
